import { Registration, RegistrationFormData } from '../types';
import { appConfig } from '../config/appConfig';

// Helper function to get admin token
const getAdminToken = (): string => {
  return btoa(`${appConfig.admin.username}:${appConfig.admin.password}`);
};

// Submit registration form
export const submitRegistration = async (formData: RegistrationFormData): Promise<Registration> => {
  try {
    console.log('Registration service - sending data:', formData);
    console.log('Tour ID being sent:', formData.tourId);
    
    const response = await fetch('/api/registrations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Registration API Error:', errorData);
      console.error('Response status:', response.status);
      console.error('Response headers:', Object.fromEntries(response.headers.entries()));
      
      // Handle validation errors with more detail
      if (errorData.details) {
        console.error('Validation details:', errorData.details);
        const fieldErrors = Object.entries(errorData.details.fieldErrors || {})
          .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
          .join('; ');
        throw new Error(`Validation failed: ${fieldErrors || errorData.message || 'Please check your form data'}`);
      }
      
      throw new Error(errorData.error?.message || errorData.error || 'Failed to submit registration');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Registration submission error:', error);
    throw error;
  }
};

// Get registrations (admin only)
export const getRegistrations = async (params?: {
  tourId?: string;
  status?: 'pending' | 'completed' | 'cancelled';
  limit?: number;
  after?: string;
}): Promise<{ items: Registration[]; nextCursor: string | null }> => {
  try {
    const queryParams = new URLSearchParams();
    if (params?.tourId) queryParams.append('tourId', params.tourId);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.after) queryParams.append('after', params.after);

    const response = await fetch(`/api/registrations?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please check admin credentials.');
      }
      let errorMessage = 'Failed to fetch registrations';
      try {
        const errorData = await response.json();
        if (errorData.error) {
          // Handle Zod validation errors
          if (typeof errorData.error === 'object' && errorData.error.fieldErrors) {
            const fieldErrors = Object.entries(errorData.error.fieldErrors)
              .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
              .join('; ');
            errorMessage = `Validation error: ${fieldErrors}`;
          } else {
            errorMessage = errorData.error;
          }
        }
      } catch (parseError) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Get registrations error:', error);
    throw error;
  }
};

// Update registration status (admin only)
export const updateRegistrationStatus = async (
  registrationId: string,
  status: 'pending' | 'completed' | 'cancelled'
): Promise<Registration> => {
  try {
    const response = await fetch(`/api/registrations/${registrationId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please check admin credentials.');
      }
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update registration');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Update registration error:', error);
    throw error;
  }
};
